import { useCallback } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useDispatch, useSelector } from 'react-redux';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import { getLabelMessage } from '@gef-ui/localization/messages';
import CircleInfo from '@gef-ui/material-icons/CircleInfo';
import Share from '@gef-ui/material-icons/Share';

import Subheader from 'components/atoms/GefMui/Subheader';
import ToolbarTemplate from 'components/atoms/GefMui/ToolbarTemplate/ToolbarTemplate';
import MODALS from 'constants/modals';
import { MPU_FORWARD_TASK } from 'features/common/acl/constants';
import { clear as clearDialog } from 'features/common/dialog/actions';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import { checkIfProcessable, processInformation } from 'features/other/processInformation/actions';
import { forwardTasks } from 'features/views/taskSearch/actions';
import {
	getHasSelectedTasks,
	getSelectedTasks,
	getSelectedTasksAreInformationOnly,
} from 'features/views/taskSearch/selectors';

export const Toolbar = () => {
	const dispatch = useDispatch();

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: getLabelMessage('taskSearch:header'),
		backButton: true,
	});

	const hasSelectedTasks = useSelector(getHasSelectedTasks);
	const areAllSelectedTasksInformationOnly = useSelector(getSelectedTasksAreInformationOnly);
	const selectedTasks = useSelector(getSelectedTasks);

	const handleTaskForward = useCallback(async () => {
		const processable = await dispatch(
			checkIfProcessable({ items: selectedTasks, page: 'taskSearch', type: 'forward' }),
		);

		if (processable) {
			dispatch(forwardTasks());
			dispatch(clearDialog());
			NiceModal.show(MODALS.task_forwarding);
		}
	}, [dispatch, selectedTasks]);

	const handleProcessInformation = useCallback(async () => {
		const processable = await dispatch(
			checkIfProcessable({ items: selectedTasks, page: 'taskSearch', type: 'process' }),
		);
		if (processable) {
			dispatch(clearDialog());
			NiceModal.show(MODALS.lightbox_modal, {
				title: getLabelMessage('lightBox:informationAssigmentAll'),
				onConfirm: () => dispatch(processInformation(selectedTasks)),
			});
		}
	}, [dispatch, selectedTasks]);

	const subheaderToolbar = (
		<Stack
			spacing={1}
			direction="row"
			sx={{
				justifyContent: 'flex-end',
			}}
		>
			<AccessControl accessLevel={MPU_FORWARD_TASK}>
				<Button
					variant="text"
					size="small"
					startIcon={<Share />}
					onClick={handleTaskForward}
					disabled={!hasSelectedTasks}
				>
					{getLabelMessage('taskSearch:forwardTask')}
				</Button>
			</AccessControl>
			<Button
				variant="text"
				size="small"
				startIcon={<CircleInfo />}
				onClick={handleProcessInformation}
				disabled={!hasSelectedTasks || !areAllSelectedTasksInformationOnly}
			>
				{getLabelMessage('taskSearch:processInformation')}
			</Button>
		</Stack>
	);

	return <ToolbarTemplate leftSide={<Subheader title={subheaderTitle} />} rightSide={subheaderToolbar} />;
};

export default Toolbar;
